// Tap an unclocked stretch of the Day timeline to log time there.
//
// The spine is a linear map from vertical pixels to time: hour H sits at
// `H * hourPx - firstHour * hourPx`. We invert that at the tap point and
// dispatch `te:new` with the resulting wall-clock time; the te-edit-modal
// controller opens the same modal used for editing, pre-filled from it.
//
// Taps that land on an existing entry (its spine segment or its bubble) are
// left alone — those already mean "edit that entry". The server does the
// real placement work: it snaps the rough time to a quarter hour and fits
// the entry into the free space that follows.
import { Controller } from "@hotwired/stimulus"

const SKIP_SELECTOR = ".te-segment, .te-bubble, a, button, input, select"

export default class extends Controller<HTMLElement> {
  static values = {
    date: String,      // "YYYY-MM-DD" of the day being shown
    firstHour: Number, // hour at the top of the rendered spine
    hourPx: Number,    // vertical pixels per hour
  }

  declare readonly dateValue: string
  declare readonly firstHourValue: number
  declare readonly hourPxValue: number

  create(event: PointerEvent | MouseEvent): void {
    const target = event.target as HTMLElement | null
    if (target?.closest(SKIP_SELECTOR)) return

    const rect = this.element.getBoundingClientRect()
    const y = event.clientY - rect.top
    const minutes = (y / this.hourPxValue + this.firstHourValue) * 60
    // The spine can run past midnight when bubbles stack; clamp so a tap
    // below the last hour still lands on this day.
    const clamped = Math.min(24 * 60 - 1, Math.max(0, Math.round(minutes)))
    const hh = String(Math.floor(clamped / 60)).padStart(2, "0")
    const mm = String(clamped % 60).padStart(2, "0")

    document.dispatchEvent(new CustomEvent("te:new", {
      detail: { startedAt: `${this.dateValue}T${hh}:${mm}:00` },
    }))
  }
}
